import { firestore } from "../firebase";
import DB_PATH from "../DB_PATH";
import {
  CommunityFormatProfile,
  CommunityFormatProfileEntry,
  CommunityCustomFormat,
  CommunityVisualStyleProfile,
  CommunityVisualStyleReference,
  CommunityAdFormat,
  CommunityAdFormatProfile,
  CommunityAdFormatProfileEntry,
  AdPlatform,
  AdAssetType,
  VoiceOfCustomerQuote,
  VoiceOfCustomerQuoteTag,
} from "../../types/CommunityMediaConfig";
import { GEMINI_API_KEY } from "../../components/screens/plan/dayplan/components/dailyStep/mediaGeneratorConfig";

// Stable Gemini model that supports the googleSearch grounding tool.
const GEMINI_MODEL = "gemini-2.5-flash";
const GEMINI_BASE = "https://generativelanguage.googleapis.com/v1beta";

export type ResearchAudienceInput = {
  communityId: string;
  niche?: string;
  topics?: string[];
  /** If true, the result is written back to mediaConfig directly. */
  apply?: boolean;
};

export type ResearchAudienceResult = {
  buyerPersona: string;
  quotes: VoiceOfCustomerQuote[];
  sources: string[];
  rationale?: string;
  applied: boolean;
};

export type ResearchFormatProfileInput = {
  communityId: string;
  /** If true, the result is written back to mediaConfig.formatProfile. */
  apply?: boolean;
};

export type DiscoveredFormatSuggestion = {
  /** Auto-generated slug from label, e.g. "tweet-reaction". */
  key: string;
  /** Human label, e.g. "Tweet + reaction". */
  label: string;
  /** Optional icon (emoji). */
  icon?: string;
  /** Slide-by-slide structure description — used as the prompt template. */
  structure: string;
  /** Psychological lever this new format pulls. */
  lever?: string;
  /** Evidence from research — accounts/posts where this was observed. */
  evidence?: string;
  /** Weight from research (0-100). */
  weight: number;
};

export type ResearchFormatProfileResult = {
  formatProfile: CommunityFormatProfile;
  /** Net-new formats the model discovered that aren't in the built-in library. */
  discoveredFormats: DiscoveredFormatSuggestion[];
  sources: string[];
  rationale?: string;
  applied: boolean;
};

export type ResearchVisualStyleInput = {
  communityId: string;
  /** If true, the result is written to mediaConfig.visualStyleProfile. */
  apply?: boolean;
};

export type ResearchVisualStyleResult = {
  visualStyleProfile: CommunityVisualStyleProfile;
  sources: string[];
  rationale?: string;
  applied: boolean;
};

export type ResearchFormatElaborationInput = {
  communityId: string;
  /** The lightweight format brief we want to deepen. */
  format: {
    key: string;
    label: string;
    icon?: string;
    structure: string;
    lever?: string;
    evidence?: string;
  };
};

export type ResearchFormatElaborationResult = {
  /** Rich slide-by-slide guide that replaces the brief structure. */
  structure: string;
  /** 2-3 example slide-1 hooks tailored to this audience + format. */
  exampleHooks: string[];
  /** Anti-patterns specific to this format + this audience. */
  antiPatterns: string[];
  sources: string[];
};

const ALLOWED_TAGS: Set<VoiceOfCustomerQuoteTag> = new Set<VoiceOfCustomerQuoteTag>([
  "pain",
  "desire",
  "identity",
  "objection",
  "language",
]);

// ─── Community context loader ─────────────────────────────────────────────

type CommunityContextSnapshot = {
  title: string;
  description: string;
  shortDescription: string;
  categoryName: string;
  tags: string[];
  existingBuyerPersona: string;
};

const loadCommunityContext = async (
  communityId: string
): Promise<CommunityContextSnapshot> => {
  const [communitySnap, mediaConfigSnap] = await Promise.all([
    firestore.collection(DB_PATH.COMMUNITY).doc(communityId).get(),
    firestore
      .collection(DB_PATH.COMMUNITY)
      .doc(communityId)
      .collection(DB_PATH.COMMUNITY_MEDIA)
      .doc("mediaConfig")
      .get(),
  ]);
  const community = (communitySnap.data() || {}) as Record<string, unknown>;
  const mediaConfig = (mediaConfigSnap.data() || {}) as Record<string, unknown>;

  return {
    title: typeof community.title === "string" ? community.title : "",
    description: typeof community.description === "string" ? community.description : "",
    shortDescription:
      typeof community.shortDescription === "string" ? community.shortDescription : "",
    categoryName: typeof community.categoryName === "string" ? community.categoryName : "",
    tags: Array.isArray(community.tags) ? (community.tags as string[]) : [],
    existingBuyerPersona:
      (typeof mediaConfig.buyerPersona === "string" && mediaConfig.buyerPersona) ||
      (typeof community.buyerPersona === "string" && community.buyerPersona) ||
      "",
  };
};

// ─── Prompt builder (mirrors the previous Cloud Function) ────────────────

const buildResearchPrompt = (
  context: CommunityContextSnapshot,
  overrides: { niche?: string; topics?: string[] }
): string => {
  const niche = overrides.niche || context.categoryName || context.title || "";
  const topicLine = overrides.topics?.length
    ? `Focus topics: ${overrides.topics.join(", ")}.`
    : "";
  const existing = context.existingBuyerPersona?.trim()
    ? `\n\nThe coach has already drafted this buyer persona — refine and extend it, do not replace its core identity:\n${context.existingBuyerPersona.trim()}`
    : "";

  return `You are a customer-research analyst. Use Google Search to find where the audience for the community below actually hangs out online (subreddits, niche forums, IG comment sections of similar creators, G2/Capterra reviews of adjacent tools, YouTube comment sections, Substack reader comments).

Pull at least 10 verbatim phrases the audience uses — exact wording, in quotes, with the source URL or platform name. Phrases should reflect pain, desire, identity, objections, or distinctive vocabulary. Do NOT paraphrase, do NOT generate quotes — only return phrases you actually grounded in search results.

Then synthesize a one-paragraph buyer persona (around 80–140 words) that names the typical member, their daily reality, what they want, what they're afraid of, what they've already tried, and the way they talk. Keep it concrete — no MBA jargon.

COMMUNITY CONTEXT:
- Name: ${context.title || "(unknown)"}
- Niche: ${niche || "(unknown)"}
- Category: ${context.categoryName || "(unknown)"}
- Description: ${context.description || "(none)"}
- Short description: ${context.shortDescription || "(none)"}
- Tags: ${context.tags?.length ? context.tags.join(", ") : "(none)"}
${topicLine}${existing}

OUTPUT FORMAT — return ONLY valid JSON, no markdown, no commentary, in exactly this shape:

{
  "buyerPersona": "one paragraph, 80-140 words",
  "rationale": "1-2 sentences naming the watering holes you mined and any caveat the coach should know",
  "quotes": [
    {
      "text": "exact verbatim phrase the audience uses",
      "source": "Reddit r/<sub> | G2 review of <tool> | IG comment on @<handle> | <other platform/URL>",
      "tag": "pain" | "desire" | "identity" | "objection" | "language"
    }
  ]
}

Return between 10 and 20 quotes. Tag each one accurately. If you can't ground something, omit it — never invent.`;
};

// ─── Gemini call (browser-direct, with Google Search grounding) ──────────

interface GeminiGroundingChunk {
  web?: { uri?: string; title?: string };
}
interface GeminiCandidate {
  content?: { parts?: { text?: string }[] };
  finishReason?: string;
  groundingMetadata?: {
    groundingChunks?: GeminiGroundingChunk[];
  };
}
interface GeminiResponse {
  candidates?: GeminiCandidate[];
  usageMetadata?: {
    promptTokenCount?: number;
    candidatesTokenCount?: number;
    totalTokenCount?: number;
  };
}

const callGeminiWithSearch = async (
  prompt: string
): Promise<{ text: string; sources: string[] }> => {
  if (!GEMINI_API_KEY) {
    throw new Error(
      "REACT_APP_GEMINI_API_KEY is not configured in the browser environment."
    );
  }

  const body = {
    contents: [{ role: "user", parts: [{ text: prompt }] }],
    tools: [{ googleSearch: {} }],
    generationConfig: {
      temperature: 0.4,
      // Gemini 2.5 Flash burns output tokens on internal "thinking" — leaving
      // headroom is critical, otherwise the response comes back empty with
      // finishReason: MAX_TOKENS. We also disable thinking entirely for these
      // structured-JSON tasks (mirrors OnboardingAPI's pattern).
      maxOutputTokens: 16384,
      thinkingConfig: { thinkingBudget: 0 },
    },
  };

  const url = `${GEMINI_BASE}/models/${GEMINI_MODEL}:generateContent?key=${encodeURIComponent(GEMINI_API_KEY)}`;
  const response = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });

  const responseText = await response.text();
  if (!response.ok) {
    throw new Error(
      `Gemini request failed (${response.status}): ${responseText}`
    );
  }

  let parsed: GeminiResponse;
  try {
    parsed = JSON.parse(responseText);
  } catch {
    throw new Error("Gemini returned a non-JSON response.");
  }

  const candidate = parsed.candidates?.[0];

  // Surface MAX_TOKENS / safety blocks as explicit errors so callers don't
  // try to parse an empty body.
  if (candidate?.finishReason && candidate.finishReason !== "STOP") {
    if (candidate.finishReason === "MAX_TOKENS") {
      throw new Error(
        `Gemini hit MAX_TOKENS before producing output (this usually means the model spent all output tokens on thinking — already disabled, so the prompt may also be too long). Try a shorter prompt or simpler audience snapshot.`
      );
    }
    throw new Error(
      `Gemini finished with ${candidate.finishReason} and returned no usable content.`
    );
  }

  const text = (candidate?.content?.parts || [])
    .map((p) => p.text || "")
    .join("");
  const sources = (candidate?.groundingMetadata?.groundingChunks || [])
    .map((c) => c.web?.uri)
    .filter((uri): uri is string => Boolean(uri));

  return { text, sources: Array.from(new Set(sources)) };
};

// ─── JSON extraction ──────────────────────────────────────────────────────

interface ParsedResearch {
  buyerPersona: string;
  rationale?: string;
  quotes: VoiceOfCustomerQuote[];
}

const slugifyFormatKey = (input: string): string =>
  input
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 32);

const stripCodeFence = (text: string): string =>
  text.replace(/^```(?:json)?\s*/i, "").replace(/```\s*$/i, "").trim();

const parseResearchJson = (raw: string): ParsedResearch => {
  let body = stripCodeFence(raw);
  if (!body.startsWith("{")) {
    const first = body.indexOf("{");
    const last = body.lastIndexOf("}");
    if (first >= 0 && last > first) body = body.slice(first, last + 1);
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(body);
  } catch (err) {
    throw new Error(`Gemini response was not valid JSON: ${(err as Error).message}`);
  }

  if (!parsed || typeof parsed !== "object") {
    throw new Error("Gemini response did not contain a JSON object.");
  }
  const obj = parsed as Record<string, unknown>;

  const buyerPersona =
    typeof obj.buyerPersona === "string" ? obj.buyerPersona.trim() : "";
  const rationale =
    typeof obj.rationale === "string" ? obj.rationale.trim() : undefined;

  const rawQuotes = Array.isArray(obj.quotes) ? obj.quotes : [];
  const quotes: VoiceOfCustomerQuote[] = [];
  rawQuotes.forEach((entry) => {
    if (!entry || typeof entry !== "object") return;
    const rec = entry as Record<string, unknown>;
    const text = typeof rec.text === "string" ? rec.text.trim() : "";
    if (!text) return;
    const source =
      typeof rec.source === "string" && rec.source.trim()
        ? rec.source.trim()
        : undefined;
    const tagRaw =
      typeof rec.tag === "string" ? rec.tag.trim().toLowerCase() : "";
    const tag = ALLOWED_TAGS.has(tagRaw as VoiceOfCustomerQuoteTag)
      ? (tagRaw as VoiceOfCustomerQuoteTag)
      : undefined;
    quotes.push({ text, source, tag });
  });

  if (!buyerPersona && quotes.length === 0) {
    throw new Error("Gemini returned no usable persona or quotes.");
  }

  return { buyerPersona, rationale, quotes };
};

// ─── Optional Firestore write ─────────────────────────────────────────────

const persistAudienceToMediaConfig = async (
  communityId: string,
  buyerPersona: string,
  quotes: VoiceOfCustomerQuote[]
): Promise<void> => {
  const now = new Date().toISOString();
  await firestore
    .collection(DB_PATH.COMMUNITY)
    .doc(communityId)
    .collection(DB_PATH.COMMUNITY_MEDIA)
    .doc("mediaConfig")
    .set(
      {
        buyerPersona,
        voiceOfCustomer: { quotes, lastResearchAt: now },
      },
      { merge: true }
    );
};

const persistFormatProfileToMediaConfig = async (
  communityId: string,
  formatProfile: CommunityFormatProfile
): Promise<void> => {
  if (!formatProfile.formats.length) return;
  await firestore
    .collection(DB_PATH.COMMUNITY)
    .doc(communityId)
    .collection(DB_PATH.COMMUNITY_MEDIA)
    .doc("mediaConfig")
    .set(
      {
        formatProfile: {
          formats: formatProfile.formats,
          lastResearchAt: new Date().toISOString(),
        },
      },
      { merge: true }
    );
};

// ─── Public API ───────────────────────────────────────────────────────────

export const researchCommunityAudience = async (
  input: ResearchAudienceInput
): Promise<ResearchAudienceResult> => {
  const communityId = (input?.communityId || "").trim();
  if (!communityId) {
    throw new Error("communityId is required.");
  }

  const context = await loadCommunityContext(communityId);
  const prompt = buildResearchPrompt(context, {
    niche: input.niche,
    topics: input.topics,
  });

  const { text, sources } = await callGeminiWithSearch(prompt);
  const parsed = parseResearchJson(text);

  const applied = Boolean(input.apply);
  if (applied) {
    await persistAudienceToMediaConfig(
      communityId,
      parsed.buyerPersona,
      parsed.quotes
    );
  }

  return {
    buyerPersona: parsed.buyerPersona,
    quotes: parsed.quotes,
    sources,
    rationale: parsed.rationale,
    applied,
  };
};

// ─── Format profile research (uses saved audience data as input) ──────────

type AudienceSnapshotForFormat = {
  buyerPersona: string;
  quotes: VoiceOfCustomerQuote[];
  communityTitle: string;
  communityNiche: string;
  communityDescription: string;
  communityTags: string[];
};

const loadAudienceSnapshot = async (
  communityId: string
): Promise<AudienceSnapshotForFormat> => {
  const [communitySnap, mediaConfigSnap] = await Promise.all([
    firestore.collection(DB_PATH.COMMUNITY).doc(communityId).get(),
    firestore
      .collection(DB_PATH.COMMUNITY)
      .doc(communityId)
      .collection(DB_PATH.COMMUNITY_MEDIA)
      .doc("mediaConfig")
      .get(),
  ]);
  const community = (communitySnap.data() || {}) as Record<string, unknown>;
  const mediaConfig = (mediaConfigSnap.data() || {}) as Record<string, unknown>;

  const buyerPersona =
    (typeof mediaConfig.buyerPersona === "string" && mediaConfig.buyerPersona) ||
    (typeof community.buyerPersona === "string" && community.buyerPersona) ||
    "";

  const vocBlock = mediaConfig.voiceOfCustomer as
    | { quotes?: unknown }
    | undefined;
  const rawQuotes = Array.isArray(vocBlock?.quotes) ? vocBlock!.quotes : [];
  const quotes: VoiceOfCustomerQuote[] = [];
  rawQuotes.forEach((entry) => {
    if (!entry || typeof entry !== "object") return;
    const rec = entry as Record<string, unknown>;
    const text = typeof rec.text === "string" ? rec.text.trim() : "";
    if (!text) return;
    const source =
      typeof rec.source === "string" && rec.source.trim() ? rec.source.trim() : undefined;
    const tagRaw =
      typeof rec.tag === "string" ? rec.tag.trim().toLowerCase() : "";
    const tag = ALLOWED_TAGS.has(tagRaw as VoiceOfCustomerQuoteTag)
      ? (tagRaw as VoiceOfCustomerQuoteTag)
      : undefined;
    quotes.push({ text, source, tag });
  });

  return {
    buyerPersona,
    quotes,
    communityTitle: typeof community.title === "string" ? community.title : "",
    communityNiche: typeof community.categoryName === "string" ? community.categoryName : "",
    communityDescription:
      typeof community.description === "string" ? community.description : "",
    communityTags: Array.isArray(community.tags) ? (community.tags as string[]) : [],
  };
};

const buildFormatProfilePrompt = (snapshot: AudienceSnapshotForFormat): string => {
  const quotesBlock = snapshot.quotes.length
    ? snapshot.quotes
        .slice(0, 25)
        .map((q) => {
          const tag = q.tag ? `[${q.tag}] ` : "";
          const src = q.source ? ` (${q.source})` : "";
          return `- ${tag}"${q.text}"${src}`;
        })
        .join("\n")
    : "(no audience quotes saved yet)";

  return `You are a viral-content strategist. The community below has ALREADY researched their audience — you have their verified buyer persona and verbatim audience quotes as input. Your job is to discover which Instagram / TikTok carousel formats this specific audience responds to best.

Use Google Search to find top-performing creators / accounts who target audiences MATCHING this persona (not just the same general category — the same emotional profile, the same vocabulary, the same pain points). Analyze the SHAPE of their best-performing carousels and reels. Pick the top 3–5 formats this audience is most likely to save, share, and follow for.

There is no pre-existing format library to constrain you. Describe each winning format you observe — give it a label, a slug, a slide-by-slide structure, and the psychological lever it pulls. Variety is the point — return whatever shapes actually win, even if they're unconventional.

AUDIENCE PROFILE (already researched — use as input, do not re-research):

Buyer persona:
${snapshot.buyerPersona || "(not yet defined)"}

Verbatim audience quotes:
${quotesBlock}

Community surface:
- Name: ${snapshot.communityTitle || "(unknown)"}
- Niche: ${snapshot.communityNiche || "(unknown)"}
- Description: ${snapshot.communityDescription || "(none)"}
- Tags: ${snapshot.communityTags.length ? snapshot.communityTags.join(", ") : "(none)"}

Examples of carousel formats you might see winning (NOT a closed list — describe whatever you actually observe):
- "hard-truth": contrarian insight challenging a common belief
- "real-world-story": dramatic story involving a specific person in your life
- "before-after": clear transformation arc from old state to new state
- "step-by-step": practical walkthrough of concrete actions
- "soft-footage-reel": ambient, mood-driven, B-roll-style captions
- "infographic": data-driven stats, facts, named concepts
- "ai-influencer": self-aware AI persona teaching a system
- "tweet-reaction": screenshot of a tweet on slide 1, then POV reaction
- "split-screen-comparison": two-column 'A vs B' slides
- "screenshot-deck": real DMs, comments, or screenshots arranged as content
- "quote-on-color": single-sentence quote on solid color
- "annotated-walkthrough": real photo with text annotations
- "ritual-checklist": repeatable daily/weekly habit list with checkboxes

DECISION RULES:
1. Match the audience's emotional state (from quotes + persona) to the format's psychological lever.
2. Look at REAL top-performing accounts you can find via search — not assumptions.
3. Pick 3–5 formats. Assign weight (0–100) for each. Weights sum to roughly 100.
4. For each, provide one-sentence evidence from a real account.
5. Never invent. If you can't ground an observation, omit the format.

OUTPUT FORMAT — return ONLY valid JSON, no markdown, no commentary, exactly this shape:

{
  "rationale": "2-3 sentences naming the top accounts you analyzed and the dominant emotional pull you saw",
  "formats": [
    {
      "key": "<kebab-case slug, e.g. tweet-reaction or hard-truth>",
      "label": "Human label, e.g. 'Tweet + reaction'",
      "icon": "<single emoji>",
      "structure": "Slide-by-slide description tailored to this audience. Slide 1: ... Slide 2: ... etc. Reference the audience's vocabulary and pain points.",
      "lever": "psychological lever, e.g. 'curiosity + social proof'",
      "evidence": "one-sentence observation from a real account where this format won",
      "weight": <number 0-100>
    }
  ]
}

Return 3–5 entries.`;
};

interface ParsedFormatResearch {
  rationale?: string;
  formatProfile: CommunityFormatProfile;
  discoveredFormats: DiscoveredFormatSuggestion[];
}

const parseFormatResearchJson = (raw: string): ParsedFormatResearch => {
  let body = stripCodeFence(raw);
  if (!body.startsWith("{")) {
    const first = body.indexOf("{");
    const last = body.lastIndexOf("}");
    if (first >= 0 && last > first) body = body.slice(first, last + 1);
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(body);
  } catch (err) {
    throw new Error(`Gemini response was not valid JSON: ${(err as Error).message}`);
  }

  if (!parsed || typeof parsed !== "object") {
    throw new Error("Gemini response did not contain a JSON object.");
  }
  const obj = parsed as Record<string, unknown>;

  const rationale =
    typeof obj.rationale === "string" ? obj.rationale.trim() : undefined;

  const rawFormats = Array.isArray(obj.formats) ? obj.formats : [];
  const seen = new Set<string>();
  const formats: CommunityFormatProfileEntry[] = [];
  const discoveredFormats: DiscoveredFormatSuggestion[] = [];

  rawFormats.forEach((entry) => {
    if (!entry || typeof entry !== "object") return;
    const rec = entry as Record<string, unknown>;
    const label = typeof rec.label === "string" ? rec.label.trim() : "";
    const structure =
      typeof rec.structure === "string" ? rec.structure.trim() : "";
    if (!label || !structure) return;
    const keyRaw =
      typeof rec.key === "string" && rec.key.trim() ? rec.key.trim() : label;
    const key = slugifyFormatKey(keyRaw);
    if (!key || seen.has(key)) return;
    seen.add(key);

    const weightRaw =
      typeof rec.weight === "number" ? rec.weight : Number(rec.weight);
    const weight = Number.isFinite(weightRaw)
      ? Math.max(0, Math.min(100, weightRaw))
      : 0;
    const icon =
      typeof rec.icon === "string" && rec.icon.trim() ? rec.icon.trim() : undefined;
    const lever =
      typeof rec.lever === "string" && rec.lever.trim() ? rec.lever.trim() : undefined;
    const evidence =
      typeof rec.evidence === "string" && rec.evidence.trim() ? rec.evidence.trim() : undefined;

    formats.push({ format: key, weight, evidence });
    discoveredFormats.push({ key, label, icon, structure, lever, evidence, weight });
  });

  formats.sort((a, b) => b.weight - a.weight);
  discoveredFormats.sort((a, b) => b.weight - a.weight);

  if (formats.length === 0) {
    throw new Error(
      "Gemini did not return any formats. Try again, or add more audience quotes first."
    );
  }

  return {
    rationale,
    formatProfile: { formats },
    discoveredFormats,
  };
};

const persistDiscoveredCustomFormats = async (
  communityId: string,
  discoveries: DiscoveredFormatSuggestion[]
): Promise<void> => {
  if (!discoveries.length) return;
  const now = new Date().toISOString();

  // Read existing customFormats and merge — never overwrite a manually edited
  // entry with the same key.
  const ref = firestore
    .collection(DB_PATH.COMMUNITY)
    .doc(communityId)
    .collection(DB_PATH.COMMUNITY_MEDIA)
    .doc("mediaConfig");
  const snap = await ref.get();
  const existing = ((snap.data() || {}) as Record<string, unknown>).customFormats;
  const existingArr: CommunityCustomFormat[] = Array.isArray(existing)
    ? (existing as CommunityCustomFormat[])
    : [];
  const existingKeys = new Set(existingArr.map((f) => f.key));

  const additions: CommunityCustomFormat[] = discoveries
    .filter((d) => !existingKeys.has(d.key))
    .map((d) => ({
      key: d.key,
      label: d.label,
      icon: d.icon,
      structure: d.structure,
      lever: d.lever,
      evidence: d.evidence,
      source: "research" as const,
      createdAt: now,
    }));

  if (additions.length === 0) return;

  await ref.set(
    { customFormats: [...existingArr, ...additions] },
    { merge: true }
  );
};

export const researchFormatProfileForAudience = async (
  input: ResearchFormatProfileInput
): Promise<ResearchFormatProfileResult> => {
  const communityId = (input?.communityId || "").trim();
  if (!communityId) {
    throw new Error("communityId is required.");
  }

  const snapshot = await loadAudienceSnapshot(communityId);
  if (!snapshot.buyerPersona.trim() && snapshot.quotes.length === 0) {
    throw new Error(
      "Run audience research first — there's no buyer persona or audience voice saved for this community yet."
    );
  }

  const prompt = buildFormatProfilePrompt(snapshot);
  const { text, sources } = await callGeminiWithSearch(prompt);
  const parsed = parseFormatResearchJson(text);

  const applied = Boolean(input.apply);
  if (applied) {
    await persistFormatProfileToMediaConfig(communityId, parsed.formatProfile);
    await persistDiscoveredCustomFormats(communityId, parsed.discoveredFormats);
  }

  return {
    formatProfile: parsed.formatProfile,
    discoveredFormats: parsed.discoveredFormats,
    sources,
    rationale: parsed.rationale,
    applied,
  };
};

// ─── Visual-style research (uses saved audience data as input) ───────────

const buildVisualStylePrompt = (snapshot: AudienceSnapshotForFormat): string => {
  const quotesBlock = snapshot.quotes.length
    ? snapshot.quotes
        .slice(0, 15)
        .map((q) => {
          const tag = q.tag ? `[${q.tag}] ` : "";
          return `- ${tag}"${q.text}"`;
        })
        .join("\n")
    : "(no audience quotes saved yet)";

  return `You are an art-direction researcher. The community below has ALREADY had its audience researched — you have the buyer persona and verbatim audience quotes as input. Your job is to discover the visual aesthetic that wins for this specific audience on Instagram and TikTok.

Use Google Search to find top-performing accounts that target audiences MATCHING this persona (same emotional profile, same vocabulary, same pain points — not just the same general category). Look at their carousels, Reels, and feed grid. Identify the SHARED visual aesthetic across the winners.

Describe the aesthetic in a way that can drop straight into an AI image-generation prompt. Be concrete and visual — palette, lighting, composition, subject treatment, era, mood. No marketing jargon, no abstract feelings.

AUDIENCE PROFILE:

Buyer persona:
${snapshot.buyerPersona || "(not yet defined)"}

Verbatim audience quotes:
${quotesBlock}

Community surface:
- Name: ${snapshot.communityTitle || "(unknown)"}
- Niche: ${snapshot.communityNiche || "(unknown)"}
- Description: ${snapshot.communityDescription || "(none)"}
- Tags: ${snapshot.communityTags.length ? snapshot.communityTags.join(", ") : "(none)"}

What to cover in the description (3-5 sentences):
- Palette: warm/cool, saturated/muted, dark/light, dominant colors.
- Lighting: natural / golden hour / soft window / studio / dim / harsh / film.
- Composition: close-up / wide / centered / off-center / negative space / flat lay / grid.
- Subject treatment: faces / hands / objects / environments / no people / candid / posed.
- Era / texture: digital / film grain / polaroid / hyperreal / minimalist / illustrated / collage.
- Mood: serene / energetic / intimate / aspirational / gritty / clean / mystical / nostalgic.

DECISION RULES:
1. Look at REAL top-performing accounts in this niche. Don't guess.
2. Identify the dominant style — what wins, not what exists.
3. If the audience is fragmented across multiple aesthetics, pick the strongest one and note the fallback.
4. Never invent. Ground every observation in something you saw.

OUTPUT FORMAT — return ONLY valid JSON, no markdown, no commentary, exactly this shape:

{
  "rationale": "2-3 sentences naming the top accounts you analyzed and the dominant aesthetic pull you saw",
  "visualStyle": {
    "description": "3-5 sentence description ready to drop into an AI image-generation prompt. Concrete, visual, specific.",
    "tags": ["tag1", "tag2", "..."],
    "references": [
      { "account": "@xxx or URL", "observation": "one-sentence note about their visual style" }
    ]
  }
}

Rules:
- "tags" should be 5-10 short style cues (each 2-5 words). Examples: "golden hour", "muted earth tones", "close-up hands", "film grain", "soft window light", "minimalist composition".
- "references" should be 2-5 real accounts you observed.
- "description" is the most important field — it's injected verbatim into every image generation prompt.`;
};

interface ParsedVisualStyleResearch {
  rationale?: string;
  visualStyleProfile: CommunityVisualStyleProfile;
}

const parseVisualStyleResearchJson = (
  raw: string
): ParsedVisualStyleResearch => {
  let body = stripCodeFence(raw);
  if (!body.startsWith("{")) {
    const first = body.indexOf("{");
    const last = body.lastIndexOf("}");
    if (first >= 0 && last > first) body = body.slice(first, last + 1);
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(body);
  } catch (err) {
    throw new Error(
      `Gemini response was not valid JSON: ${(err as Error).message}`
    );
  }

  if (!parsed || typeof parsed !== "object") {
    throw new Error("Gemini response did not contain a JSON object.");
  }
  const obj = parsed as Record<string, unknown>;

  const rationale =
    typeof obj.rationale === "string" ? obj.rationale.trim() : undefined;

  const styleObj =
    obj.visualStyle && typeof obj.visualStyle === "object"
      ? (obj.visualStyle as Record<string, unknown>)
      : {};

  const description =
    typeof styleObj.description === "string"
      ? styleObj.description.trim()
      : "";
  const tagsIn = Array.isArray(styleObj.tags) ? styleObj.tags : [];
  const tags = tagsIn
    .map((t) => (typeof t === "string" ? t.trim() : ""))
    .filter((t): t is string => Boolean(t));
  const refsIn = Array.isArray(styleObj.references) ? styleObj.references : [];
  const references: CommunityVisualStyleReference[] = [];
  refsIn.forEach((entry) => {
    if (!entry || typeof entry !== "object") return;
    const rec = entry as Record<string, unknown>;
    const account =
      typeof rec.account === "string" ? rec.account.trim() : "";
    const observation =
      typeof rec.observation === "string" ? rec.observation.trim() : "";
    if (!account || !observation) return;
    references.push({ account, observation });
  });

  if (!description && tags.length === 0) {
    throw new Error(
      "Gemini did not return a usable visual style. Try again, or add more audience quotes first."
    );
  }

  return {
    rationale,
    visualStyleProfile: {
      description,
      tags,
      ...(references.length ? { references } : {}),
    },
  };
};

const persistVisualStyleToMediaConfig = async (
  communityId: string,
  profile: CommunityVisualStyleProfile
): Promise<void> => {
  const now = new Date().toISOString();
  await firestore
    .collection(DB_PATH.COMMUNITY)
    .doc(communityId)
    .collection(DB_PATH.COMMUNITY_MEDIA)
    .doc("mediaConfig")
    .set(
      {
        visualStyleProfile: {
          description: profile.description,
          tags: profile.tags,
          ...(profile.references && profile.references.length
            ? { references: profile.references }
            : {}),
          lastResearchAt: now,
        },
      },
      { merge: true }
    );
};

export const researchVisualStyleForAudience = async (
  input: ResearchVisualStyleInput
): Promise<ResearchVisualStyleResult> => {
  const communityId = (input?.communityId || "").trim();
  if (!communityId) {
    throw new Error("communityId is required.");
  }

  const snapshot = await loadAudienceSnapshot(communityId);
  if (!snapshot.buyerPersona.trim() && snapshot.quotes.length === 0) {
    throw new Error(
      "Run audience research first — there's no buyer persona or audience voice saved for this community yet."
    );
  }

  const prompt = buildVisualStylePrompt(snapshot);
  const { text, sources } = await callGeminiWithSearch(prompt);
  const parsed = parseVisualStyleResearchJson(text);

  const applied = Boolean(input.apply);
  if (applied) {
    await persistVisualStyleToMediaConfig(communityId, parsed.visualStyleProfile);
  }

  return {
    visualStyleProfile: parsed.visualStyleProfile,
    sources,
    rationale: parsed.rationale,
    applied,
  };
};

// ─── Per-format elaboration (deep research for a single picked format) ────

const buildFormatElaborationPrompt = (
  snapshot: AudienceSnapshotForFormat,
  format: ResearchFormatElaborationInput["format"]
): string => {
  const quotesBlock = snapshot.quotes.length
    ? snapshot.quotes
        .slice(0, 12)
        .map((q) => {
          const tag = q.tag ? `[${q.tag}] ` : "";
          return `- ${tag}"${q.text}"`;
        })
        .join("\n")
    : "(no audience quotes saved yet)";

  return `You are a senior copywriting director. Your job is to write the COMPLETE creative brief for ONE specific Instagram carousel format, tailored to a specific audience.

The community has already researched the audience (persona + verbatim quotes below) AND already picked this format as a winner. You don't pick — you elaborate. Use Google Search if you need to see real examples of this format in action, but the main work is writing a detailed slide-by-slide guide that the slide-generation AI can follow precisely.

AUDIENCE PROFILE:

Buyer persona:
${snapshot.buyerPersona || "(not yet defined)"}

Verbatim audience quotes:
${quotesBlock}

Community surface:
- Name: ${snapshot.communityTitle || "(unknown)"}
- Niche: ${snapshot.communityNiche || "(unknown)"}

THE FORMAT TO ELABORATE:
- Key: ${format.key}
- Label: ${format.label}
- Brief structure (current, too thin): ${format.structure}
- Psychological lever: ${format.lever || "(unspecified)"}
- Evidence: ${format.evidence || "(none)"}

YOUR TASK — produce a detailed creative brief for a 6-slide carousel using this format for THIS audience. The brief must:

1. Define each of the 6 slides with:
   - Role (e.g. "Hook — outcome promise", "Step 1", "Save slide", etc.)
   - Concrete instructions for what to write (not vague — specific cadence, length, what to mention, what to avoid)
   - Hook patterns or templates the model can vary on (slide 1)
   - Where the audience's exact phrasing should be reused (point at specific VOC quotes when relevant)
2. Identify which slide is the SAVE TRIGGER (screenshotable on its own) and why.
3. Identify which slide is the SHARE TRIGGER (universal truth, friend-forward) and why.
4. List 2-3 example slide-1 hooks specifically tailored to this audience's pain/desire.
5. List 3-5 ANTI-PATTERNS — things that would kill this format for this audience.

CRITICAL BRAND-MENTION RULE — read carefully:
The carousel contains EXACTLY ONE brand mention. Not two. Not zero. ONE.
- Pick exactly ONE body slide (usually slide 3 or 4) where the community/brand name appears as a natural story beat ("then I found {brand}", "a practice from {brand}"), NEVER as a pitch.
- The FINAL slide (slide 6 / share trigger) MUST be a universal truth a stranger could screenshot and forward to a friend. No brand mention. No CTA. No "join us", no "follow for more", no "comment X for Y", no community invite. Just a quotable line that works on its own.
- Two brand mentions makes the deck feel like an ad and kills share rate. Be ruthless about this.
- If the format truly demands a CTA (e.g. it's a launch deck), put it on the body slide that already carries the brand mention, never on slide 6.

VOICE / POINT-OF-VIEW — pick the right one for THIS specific format:
- First person ("I", "my") — fits personal-narrative formats: real-world story, before-after, step-by-step, confessional hard-truth.
- Second person ("you", "your") — fits direct-callout formats: many hard-truth variants, before-after as a coaching pitch.
- Third person / omniscient — fits soft-footage-reel ambient narration, observational mood pieces.
- Data-fact voice (no pronouns, declarative) — fits infographic, stat-driven formats. Never first person.
- AI / non-human persona — fits ai-influencer, character-led formats. First person but explicitly non-human.
- Quote / aphorism — fits quote-on-color formats. No pronouns required, just the quote.

State the voice EXPLICITLY in the brief (see shape below). The slide-generation model will follow it. If you don't state it, downstream defaults to first person, which is often wrong.

VISUAL DIRECTION — describe the IMAGE AESTHETIC this specific format demands. This drops into every image-generation prompt for slides in this format.
- Photographic formats (real-world story, before-after, soft-footage-reel, step-by-step, hard-truth as candid scene): describe palette, lighting, shot type, composition, era. Authentic phone-shot UGC vs cinematic vs film-grain etc.
- Graphic formats (infographic, quote-on-color, split-screen-comparison): describe layout, color blocking, typography mood, icon style — NOT a photo. Specify "graphic illustration, no photos, no people".
- Screenshot-style formats (tweet-reaction, screenshot-deck): describe the UI being depicted (tweet card, DM thread) with specific color/font cues.
- AI-persona formats (ai-influencer): describe the rendered character — stylized 3D, anime, surreal, etc.
- Be concrete: name palette, lighting, composition, subject treatment. 2-4 sentences.
- Do NOT include the community-wide visual style verbatim — describe what THIS FORMAT looks like. The community style is a fallback.

State the visual direction EXPLICITLY in the brief (see shape below).

WRITE THE BRIEF AS A SINGLE STRUCTURED TEXT BLOCK ready to drop into a slide-generation system prompt. Use this exact shape:

THE FORMAT — "${format.label}" (6 slides):
Voice/POV: <e.g. "first person — 'I', 'my'" / "second person — 'you'" / "data-fact, no pronouns" / "AI persona — first person but non-human" — be explicit>
Visual direction: <2-4 sentences describing the image aesthetic for this format specifically — palette, lighting, shot type or graphic layout, mood>
Lever: <one sentence on the psychological pull>
Save trigger: Slide <N> — <why>
Share trigger: Slide <N> — <why>

Slide 1 — <role>:
<concrete instructions, 2-4 sentences>
Hook templates (vary on these):
- "<example pattern 1>"
- "<example pattern 2>"
Examples for THIS audience:
- "<example hook 1>"
- "<example hook 2>"

Slide 2 — <role>:
<instructions, 1-2 sentences>
Audience-language hint: <which VOC quote phrase to mirror here, if any>

Slide 3 — <role>:
<instructions including brand mention placement if applicable>

Slide 4 — <role>:
<instructions>

Slide 5 — <role>:
<instructions>

Slide 6 — <role>:
<instructions>

Anti-patterns:
- <pattern 1>
- <pattern 2>
- <pattern 3>

OUTPUT — return ONLY valid JSON, no markdown, no commentary, in exactly this shape:

{
  "structure": "the full brief as one structured text block, exactly the shape above",
  "exampleHooks": ["hook 1", "hook 2", "hook 3"],
  "antiPatterns": ["anti-pattern 1", "anti-pattern 2", "anti-pattern 3"]
}

Be SPECIFIC. No generic phrases like "expand on the insight". Every slide should have actionable instructions a copywriter could follow.`;
};

interface ParsedFormatElaboration {
  structure: string;
  exampleHooks: string[];
  antiPatterns: string[];
}

// Tries multiple recovery strategies because Gemini's grounded-search responses
// often include trailing commentary, embedded ``` blocks, or improperly escaped
// inner quotes in the (multi-line) structure field.
const tryParseElaborationJson = (raw: string): unknown | null => {
  const candidates: string[] = [];
  const stripped = stripCodeFence(raw);
  candidates.push(stripped);

  // Strategy 1: pull the first balanced JSON object substring.
  const first = stripped.indexOf("{");
  const last = stripped.lastIndexOf("}");
  if (first >= 0 && last > first) {
    candidates.push(stripped.slice(first, last + 1));
  }

  // Strategy 2: collapse any \r and try again.
  candidates.push(stripped.replace(/\r/g, ""));

  for (const candidate of candidates) {
    try {
      return JSON.parse(candidate);
    } catch {
      /* fall through */
    }
  }
  return null;
};

// Last-resort extractor: pull "structure": "..." out of a malformed JSON
// payload by scanning for the field literal and reading until the matching
// closing quote (handles escaped quotes inside).
const extractStringField = (raw: string, field: string): string | null => {
  const marker = `"${field}"`;
  const idx = raw.indexOf(marker);
  if (idx < 0) return null;
  // Find the opening quote after the colon.
  const colonIdx = raw.indexOf(":", idx + marker.length);
  if (colonIdx < 0) return null;
  let i = colonIdx + 1;
  while (i < raw.length && raw[i] !== '"') i += 1;
  if (i >= raw.length) return null;
  const start = i + 1;
  let end = start;
  while (end < raw.length) {
    if (raw[end] === "\\") {
      end += 2;
      continue;
    }
    if (raw[end] === '"') break;
    end += 1;
  }
  if (end >= raw.length) return null;
  // Unescape common sequences.
  return raw
    .slice(start, end)
    .replace(/\\n/g, "\n")
    .replace(/\\"/g, '"')
    .replace(/\\\\/g, "\\");
};

const parseFormatElaborationJson = (raw: string): ParsedFormatElaboration => {
  const parsed = tryParseElaborationJson(raw);

  if (parsed && typeof parsed === "object") {
    const obj = parsed as Record<string, unknown>;
    const structure =
      typeof obj.structure === "string" ? obj.structure.trim() : "";
    if (structure) {
      const exampleHooksIn = Array.isArray(obj.exampleHooks) ? obj.exampleHooks : [];
      const exampleHooks = exampleHooksIn
        .map((h) => (typeof h === "string" ? h.trim() : ""))
        .filter((h): h is string => Boolean(h));
      const antiPatternsIn = Array.isArray(obj.antiPatterns) ? obj.antiPatterns : [];
      const antiPatterns = antiPatternsIn
        .map((a) => (typeof a === "string" ? a.trim() : ""))
        .filter((a): a is string => Boolean(a));
      return { structure, exampleHooks, antiPatterns };
    }
  }

  // Recovery path: pull just the structure string out by hand.
  const rescuedStructure = extractStringField(raw, "structure");
  if (rescuedStructure && rescuedStructure.trim()) {
    return {
      structure: rescuedStructure.trim(),
      exampleHooks: [],
      antiPatterns: [],
    };
  }

  // Last resort: if the raw response actually contains slide-by-slide text
  // (matches the format we asked for), treat the whole thing as the brief.
  if (/Slide\s+1/i.test(raw) && raw.trim().length > 200) {
    return {
      structure: raw.trim(),
      exampleHooks: [],
      antiPatterns: [],
    };
  }

  throw new Error(
    `Gemini elaboration didn't parse as JSON. Raw response: ${raw.slice(0, 400)}${raw.length > 400 ? "..." : ""}`
  );
};

export const researchFormatElaboration = async (
  input: ResearchFormatElaborationInput
): Promise<ResearchFormatElaborationResult> => {
  const communityId = (input?.communityId || "").trim();
  if (!communityId) {
    throw new Error("communityId is required.");
  }
  if (!input?.format?.key || !input?.format?.label) {
    throw new Error("format.key and format.label are required.");
  }

  const snapshot = await loadAudienceSnapshot(communityId);
  const prompt = buildFormatElaborationPrompt(snapshot, input.format);
  const { text, sources } = await callGeminiWithSearch(prompt);
  const parsed = parseFormatElaborationJson(text);

  return {
    structure: parsed.structure,
    exampleHooks: parsed.exampleHooks,
    antiPatterns: parsed.antiPatterns,
    sources,
  };
};

// ─── Paid ad format research (Meta + TikTok) ─────────────────────────────
//
// Discovers which paid-ad shapes win for this audience on Meta and TikTok.
// Distinct from organic format research — paid traffic demands a 1s hook,
// offer by ~3s, hard CTA at the end, and platform-native shapes (UGC
// testimonial, founder talking-head, problem-agitate-solve, listicle, etc.).

export type ResearchAdFormatProfileInput = {
  communityId: string;
  platforms?: AdPlatform[];
  apply?: boolean;
};

export type DiscoveredAdFormat = {
  key: string;
  label: string;
  icon?: string;
  platforms: AdPlatform[];
  assetType: AdAssetType;
  structure: string;
  lever?: string;
  evidence?: string;
  targetLengthSec?: number;
  weight: number;
};

export type ResearchAdFormatProfileResult = {
  adFormatProfile: CommunityAdFormatProfile;
  discoveredAdFormats: DiscoveredAdFormat[];
  sources: string[];
  rationale?: string;
  applied: boolean;
};

const VALID_AD_PLATFORM_VALUES: AdPlatform[] = ["meta", "tiktok"];
const VALID_AD_ASSET_VALUES: AdAssetType[] = [
  "ugc-testimonial",
  "founder-talking-head",
  "problem-agitate-solve",
  "listicle",
  "before-after",
  "day-in-the-life",
  "pov-i-tried",
  "static-image",
  "carousel",
];

const buildAdFormatResearchPrompt = (
  snapshot: AudienceSnapshotForFormat,
  platforms: AdPlatform[]
): string => {
  const quotesBlock = snapshot.quotes.length
    ? snapshot.quotes
        .slice(0, 20)
        .map((q) => {
          const tag = q.tag ? `[${q.tag}] ` : "";
          return `- ${tag}"${q.text}"`;
        })
        .join("\n")
    : "(no audience quotes saved yet)";

  const platformList = platforms.length
    ? platforms.join(" and ")
    : "Meta (Facebook + Instagram) and TikTok";

  return `You are a paid-social ad strategist. The community below has ALREADY researched their audience — you have their verified buyer persona and verbatim audience quotes as input.

Your job is to discover which PAID AD formats (NOT organic carousels) win for this audience on ${platformList}. Use Google Search to find top-performing paid ads — TikTok Creative Center, Foreplay/Atria/Motion ad libraries, public Meta Ad Library, viral UGC examples on TikTok with "#ad" or "#sponsored". Identify the SHAPES of ads that drive clicks and conversions for audiences MATCHING this persona.

Paid ad shape rules (very different from organic):
- Hook MUST land in the first 1-2 seconds. Paid traffic has zero patience.
- Offer / product / brand appears by second 3, not buried.
- Hard CTA at the end ("Get yours today", "Tap to shop", "Start free trial") — NOT a universal share-worthy line.
- Length: TikTok 9-21s sweet spot, Meta 15-30s sweet spot.
- Brand can be mentioned multiple times — the brand IS the point.

AUDIENCE PROFILE (already researched — use as input, do not re-research):

Buyer persona:
${snapshot.buyerPersona || "(not yet defined)"}

Verbatim audience quotes:
${quotesBlock}

Community surface:
- Name: ${snapshot.communityTitle || "(unknown)"}
- Niche: ${snapshot.communityNiche || "(unknown)"}
- Description: ${snapshot.communityDescription || "(none)"}
- Tags: ${snapshot.communityTags.length ? snapshot.communityTags.join(", ") : "(none)"}

Examples of paid ad shapes you might find winning (NOT a closed list — describe whatever you actually observe):
- "ugc-testimonial": real-customer-style talking-head with a transformation claim
- "founder-talking-head": founder direct-to-camera, problem + product + CTA
- "problem-agitate-solve": cold-open with the pain, escalate, then product as relief
- "listicle": "3 reasons / 5 mistakes / 7 signs" countdown structure
- "before-after": clear visual transformation, product in the middle
- "day-in-the-life": product integrated naturally into routine footage
- "pov-i-tried": "I tried X for 30 days" challenge frame
- "static-image": single still + caption (Meta feed only)
- "carousel": multi-image swipe (Meta only)

DECISION RULES:
1. Match the audience's emotional state (from quotes + persona) to the ad shape's psychological lever.
2. Look at REAL top-performing paid ads via search — TikTok Creative Center, Meta Ad Library, public agency case studies.
3. Pick 3-5 ad shapes that fit. Specify which platforms each fits (Meta, TikTok, or both).
4. Assign weight (0-100) for each. Weights sum to roughly 100.
5. For each, provide one-sentence evidence from a real ad or account.
6. assetType MUST be one of: ${VALID_AD_ASSET_VALUES.join(", ")}.
7. platforms MUST be one or more of: ${VALID_AD_PLATFORM_VALUES.join(", ")}.
8. Never invent. If you can't ground an observation, omit the format.

STRUCTURE field requirements — write the beat-by-beat script brief for the ad. Specify:
- Second 0-1: visual + verbal hook (both at once)
- Second 1-3: offer/product establishment
- Middle beats: proof / demonstration / payoff
- Final 2-3 seconds: hard CTA (tap to shop / get yours / start free)
- Total target length in seconds
- Reference the audience's vocabulary and pain points from the quotes above
- Anti-pattern: NO universal share-worthy quotable lines, NO "follow for more"

OUTPUT FORMAT — return ONLY valid JSON, no markdown, no commentary, exactly this shape:

{
  "rationale": "2-3 sentences naming the top ads/accounts you analyzed and the dominant pull you saw",
  "formats": [
    {
      "key": "<kebab-case slug, e.g. ugc-testimonial-30s>",
      "label": "Human label, e.g. 'UGC testimonial (30s)'",
      "icon": "<single emoji>",
      "platforms": ["meta", "tiktok"],
      "assetType": "<one of: ${VALID_AD_ASSET_VALUES.join(" | ")}>",
      "structure": "Beat-by-beat script brief, formatted as timestamped beats. Reference audience pain/desire language. End with hard CTA.",
      "lever": "psychological lever, e.g. 'social proof + identity'",
      "evidence": "one-sentence observation from a real paid ad or library entry",
      "targetLengthSec": <number, e.g. 21>,
      "weight": <number 0-100>
    }
  ]
}

Return 3-5 entries.`;
};

interface ParsedAdFormatResearch {
  rationale?: string;
  adFormatProfile: CommunityAdFormatProfile;
  discoveredAdFormats: DiscoveredAdFormat[];
}

const parseAdFormatResearchJson = (raw: string): ParsedAdFormatResearch => {
  let body = stripCodeFence(raw);
  if (!body.startsWith("{")) {
    const first = body.indexOf("{");
    const last = body.lastIndexOf("}");
    if (first >= 0 && last > first) body = body.slice(first, last + 1);
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(body);
  } catch (err) {
    throw new Error(`Gemini ad-format response was not valid JSON: ${(err as Error).message}`);
  }

  if (!parsed || typeof parsed !== "object") {
    throw new Error("Gemini ad-format response did not contain a JSON object.");
  }
  const obj = parsed as Record<string, unknown>;

  const rationale =
    typeof obj.rationale === "string" ? obj.rationale.trim() : undefined;

  const rawFormats = Array.isArray(obj.formats) ? obj.formats : [];
  const seen = new Set<string>();
  const formats: CommunityAdFormatProfileEntry[] = [];
  const discoveredAdFormats: DiscoveredAdFormat[] = [];

  rawFormats.forEach((entry) => {
    if (!entry || typeof entry !== "object") return;
    const rec = entry as Record<string, unknown>;
    const label = typeof rec.label === "string" ? rec.label.trim() : "";
    const structure =
      typeof rec.structure === "string" ? rec.structure.trim() : "";
    if (!label || !structure) return;
    const keyRaw =
      typeof rec.key === "string" && rec.key.trim() ? rec.key.trim() : label;
    const key = slugifyFormatKey(keyRaw);
    if (!key || seen.has(key)) return;

    const platformsIn = Array.isArray(rec.platforms) ? rec.platforms : [];
    const platforms = platformsIn
      .map((p) => (typeof p === "string" ? p.trim().toLowerCase() : ""))
      .filter((p): p is AdPlatform =>
        (VALID_AD_PLATFORM_VALUES as string[]).includes(p)
      );
    if (platforms.length === 0) return;

    const assetTypeRaw =
      typeof rec.assetType === "string" ? rec.assetType.trim() : "";
    const assetType = (VALID_AD_ASSET_VALUES as string[]).includes(assetTypeRaw)
      ? (assetTypeRaw as AdAssetType)
      : "ugc-testimonial";

    seen.add(key);

    const weightRaw =
      typeof rec.weight === "number" ? rec.weight : Number(rec.weight);
    const weight = Number.isFinite(weightRaw)
      ? Math.max(0, Math.min(100, weightRaw))
      : 0;
    const icon =
      typeof rec.icon === "string" && rec.icon.trim() ? rec.icon.trim() : undefined;
    const lever =
      typeof rec.lever === "string" && rec.lever.trim() ? rec.lever.trim() : undefined;
    const evidence =
      typeof rec.evidence === "string" && rec.evidence.trim() ? rec.evidence.trim() : undefined;
    const lenRaw =
      typeof rec.targetLengthSec === "number"
        ? rec.targetLengthSec
        : Number(rec.targetLengthSec);
    const targetLengthSec = Number.isFinite(lenRaw)
      ? Math.max(3, Math.min(60, Math.round(lenRaw)))
      : undefined;

    formats.push({ format: key, weight, evidence });
    discoveredAdFormats.push({
      key,
      label,
      icon,
      platforms: Array.from(new Set(platforms)) as AdPlatform[],
      assetType,
      structure,
      lever,
      evidence,
      targetLengthSec,
      weight,
    });
  });

  formats.sort((a, b) => b.weight - a.weight);
  discoveredAdFormats.sort((a, b) => b.weight - a.weight);

  if (formats.length === 0) {
    throw new Error(
      "Gemini did not return any ad formats. Try again, or add more audience quotes first."
    );
  }

  return {
    rationale,
    adFormatProfile: { formats },
    discoveredAdFormats,
  };
};

const persistAdFormatProfileToMediaConfig = async (
  communityId: string,
  adFormatProfile: CommunityAdFormatProfile,
  discoveries: DiscoveredAdFormat[]
): Promise<void> => {
  if (!adFormatProfile.formats.length) return;
  const now = new Date().toISOString();

  const ref = firestore
    .collection(DB_PATH.COMMUNITY)
    .doc(communityId)
    .collection(DB_PATH.COMMUNITY_MEDIA)
    .doc("mediaConfig");

  const snap = await ref.get();
  const existing = ((snap.data() || {}) as Record<string, unknown>).adFormats;
  const existingArr: CommunityAdFormat[] = Array.isArray(existing)
    ? (existing as CommunityAdFormat[])
    : [];
  const existingKeys = new Set(existingArr.map((f) => f.key));

  const additions: CommunityAdFormat[] = discoveries
    .filter((d) => !existingKeys.has(d.key))
    .map((d) => ({
      key: d.key,
      label: d.label,
      icon: d.icon,
      platforms: d.platforms,
      assetType: d.assetType,
      structure: d.structure,
      lever: d.lever,
      evidence: d.evidence,
      targetLengthSec: d.targetLengthSec,
      source: "research" as const,
      createdAt: now,
    }));

  await ref.set(
    {
      adFormatProfile: {
        formats: adFormatProfile.formats,
        lastResearchAt: now,
      },
      ...(additions.length
        ? { adFormats: [...existingArr, ...additions] }
        : {}),
    },
    { merge: true }
  );
};

export const researchAdFormatsForAudience = async (
  input: ResearchAdFormatProfileInput
): Promise<ResearchAdFormatProfileResult> => {
  const communityId = (input?.communityId || "").trim();
  if (!communityId) {
    throw new Error("communityId is required.");
  }

  const snapshot = await loadAudienceSnapshot(communityId);
  if (!snapshot.buyerPersona.trim() && snapshot.quotes.length === 0) {
    throw new Error(
      "Run audience research first — there's no buyer persona or audience voice saved for this community yet."
    );
  }

  const platforms = input.platforms?.length
    ? input.platforms.filter((p): p is AdPlatform =>
        (VALID_AD_PLATFORM_VALUES as string[]).includes(p)
      )
    : VALID_AD_PLATFORM_VALUES;

  const prompt = buildAdFormatResearchPrompt(snapshot, platforms);
  const { text, sources } = await callGeminiWithSearch(prompt);
  const parsed = parseAdFormatResearchJson(text);

  const applied = Boolean(input.apply);
  if (applied) {
    await persistAdFormatProfileToMediaConfig(
      communityId,
      parsed.adFormatProfile,
      parsed.discoveredAdFormats
    );
  }

  return {
    adFormatProfile: parsed.adFormatProfile,
    discoveredAdFormats: parsed.discoveredAdFormats,
    sources,
    rationale: parsed.rationale,
    applied,
  };
};
